// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { CarePlan, Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { TREATMENT_STATUS_SYSTEM } from '@polarissleep/scheduling';
import { useCallback, useEffect, useState } from 'react';

export interface UseTreatmentPlanReturn {
  /** Free-text plan summary; falls back to the first CarePlan note if `description` isn't set. */
  description: string | undefined;
  carePlan: CarePlan | undefined;
  loading: boolean;
  error: Error | null;
}

/**
 * Pulls the patient's active CarePlan and returns its description for the
 * dashboard's Treatment Plan panel. Empty when no plan exists yet — the
 * consuming component renders the empty-state copy.
 */
export function useTreatmentPlan(): UseTreatmentPlanReturn {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const [carePlan, setCarePlan] = useState<CarePlan | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchPlan = useCallback(async () => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const plan = await medplum.searchOne('CarePlan', {
        subject: `Patient/${profile.id}`,
        status: 'active',
        'activity-code': `${TREATMENT_STATUS_SYSTEM}|`,
        _sort: '-_lastUpdated',
      });
      setCarePlan(plan as CarePlan | undefined);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load treatment plan'));
    } finally {
      setLoading(false);
    }
  }, [medplum, profile]);

  useEffect(() => {
    fetchPlan().catch(console.error);
  }, [fetchPlan]);

  const description = carePlan?.description ?? carePlan?.note?.[0]?.text;

  return { description, carePlan, loading, error };
}
